import { useEffect, useMemo, useState } from 'react'
import {
  ArrowDown,
  ArrowUp,
  Eye,
  ExternalLink,
  Image as ImageIcon,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { HomeHeroCarouselView } from '@/features/public/components/HomeHeroBanner'
import { MarkdownField } from '@/features/projectBoards/components/MarkdownField'
import {
  MAX_HOME_HERO_ROTATION_SECONDS,
  MIN_HOME_HERO_ROTATION_SECONDS,
  createHomeHeroBanner,
} from '@/lib/homeHero'
import type { HomeHeroBannerItem } from '@/types'
import { MediaPickerSheet } from '../components/MediaPickerSheet'
import { useAdminHomeHero } from '../hooks/useAdminHomeHero'

function validColorInput(color: string): string {
  return /^#[0-9a-f]{6}$/i.test(color) ? color : '#ffffff'
}

export function AdminHomeHeroPage() {
  const { values, setValues, lastUpdated, loading, error, saving, save } = useAdminHomeHero()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [bgPickerOpen, setBgPickerOpen] = useState(false)
  const [logoPickerOpen, setLogoPickerOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)

  const selectedBanner = useMemo(
    () => values.banners.find((banner) => banner.id === selectedId) ?? null,
    [selectedId, values.banners]
  )
  const activePosition = new Map(values.activeBannerIds.map((bannerId, index) => [bannerId, index]))

  useEffect(() => {
    if (!loading && !selectedId && values.banners.length > 0) {
      setSelectedId(values.banners[0]?.id ?? null)
    }
  }, [loading, selectedId, values.banners])

  function addBanner() {
    const id = crypto.randomUUID()
    const banner = createHomeHeroBanner(id, `Banner ${values.banners.length + 1}`)
    setValues((current) => ({ ...current, banners: [...current.banners, banner] }))
    setSelectedId(id)
  }

  function patchBanner<K extends keyof HomeHeroBannerItem>(key: K, value: HomeHeroBannerItem[K]) {
    if (!selectedId) return
    setValues((current) => ({
      ...current,
      banners: current.banners.map((banner) =>
        banner.id === selectedId ? { ...banner, [key]: value } : banner
      ),
    }))
  }

  function setBannerActive(bannerId: string, active: boolean) {
    setValues((current) => ({
      ...current,
      activeBannerIds: active
        ? current.activeBannerIds.includes(bannerId)
          ? current.activeBannerIds
          : [...current.activeBannerIds, bannerId]
        : current.activeBannerIds.filter((id) => id !== bannerId),
    }))
  }

  function moveActiveBanner(bannerId: string, offset: -1 | 1) {
    setValues((current) => {
      const currentIndex = current.activeBannerIds.indexOf(bannerId)
      const nextIndex = currentIndex + offset
      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= current.activeBannerIds.length) {
        return current
      }
      const activeBannerIds = [...current.activeBannerIds]
      const displacedId = activeBannerIds[nextIndex]
      if (!displacedId) return current
      activeBannerIds[currentIndex] = displacedId
      activeBannerIds[nextIndex] = bannerId
      return { ...current, activeBannerIds }
    })
  }

  function deleteSelectedBanner() {
    if (!selectedBanner) return
    const selectedIndex = values.banners.findIndex((banner) => banner.id === selectedBanner.id)
    const nextSelection =
      values.banners[selectedIndex + 1] ?? values.banners[selectedIndex - 1] ?? null
    setValues((current) => ({
      ...current,
      banners: current.banners.filter((banner) => banner.id !== selectedBanner.id),
      activeBannerIds: current.activeBannerIds.filter((id) => id !== selectedBanner.id),
    }))
    setSelectedId(nextSelection?.id ?? null)
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const previewValues =
    values.activeBannerIds.length > 0 || !selectedBanner
      ? values
      : { ...values, activeBannerIds: [selectedBanner.id] }
  const backgroundStyle = selectedBanner?.backgroundImageUrl
    ? {
        backgroundImage: `url(${selectedBanner.backgroundImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: `${selectedBanner.backgroundFocusX * 100}% ${selectedBanner.backgroundFocusY * 100}%`,
      }
    : undefined

  return (
    <div className="w-full max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Home Hero Banners</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Create reusable banners, choose which ones are live, and set their carousel order.
          </p>
          {lastUpdated && (
            <p className="mt-1 text-sm text-muted-foreground">
              Last saved{' '}
              {lastUpdated.toLocaleString('en-US', {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="h-11"
            onClick={() => setPreviewOpen(true)}
            disabled={values.banners.length === 0}
          >
            <Eye className="mr-2 h-4 w-4" />
            Preview
          </Button>
          <Button
            className="h-11"
            onClick={() => void save().catch(() => undefined)}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
        <section className="h-fit overflow-hidden rounded-lg border lg:sticky lg:top-6">
          <div className="flex items-center justify-between border-b p-4">
            <div>
              <h2 className="font-semibold">Banner Library</h2>
              <p className="text-sm text-muted-foreground">
                {values.activeBannerIds.length} active of {values.banners.length}
              </p>
            </div>
            <Button className="h-11" onClick={addBanner}>
              <Plus className="mr-2 h-4 w-4" />
              New
            </Button>
          </div>

          {values.banners.length === 0 ? (
            <div className="p-6 text-center">
              <ImageIcon className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 font-medium">No saved banners</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Create your first banner to begin.
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {values.banners.map((banner) => {
                const position = activePosition.get(banner.id)
                const active = position !== undefined
                return (
                  <div
                    key={banner.id}
                    className={selectedId === banner.id ? 'bg-muted/70' : undefined}
                  >
                    <div className="flex items-center gap-2 p-2">
                      <button
                        type="button"
                        className="min-h-11 min-w-0 flex-1 rounded-md px-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => setSelectedId(banner.id)}
                      >
                        <span className="block truncate font-medium">{banner.name}</span>
                        <span className="block text-sm text-muted-foreground">
                          {active ? `Active, position ${position + 1}` : 'Saved, not active'}
                        </span>
                      </button>
                      <Switch
                        checked={active}
                        onCheckedChange={(checked) => setBannerActive(banner.id, checked)}
                        aria-label={`${active ? 'Deactivate' : 'Activate'} ${banner.name}`}
                      />
                    </div>
                    {active && values.activeBannerIds.length > 1 && (
                      <div className="flex items-center gap-1 px-4 pb-3">
                        <span className="mr-auto text-sm text-muted-foreground">
                          Carousel position {position + 1}
                        </span>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-11 w-11"
                          onClick={() => moveActiveBanner(banner.id, -1)}
                          disabled={position === 0}
                          aria-label={`Move ${banner.name} earlier`}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-11 w-11"
                          onClick={() => moveActiveBanner(banner.id, 1)}
                          disabled={position === values.activeBannerIds.length - 1}
                          aria-label={`Move ${banner.name} later`}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          <div className="space-y-2 border-t p-4">
            <Label htmlFor="rotation-interval">Seconds per banner</Label>
            <Input
              id="rotation-interval"
              type="number"
              min={MIN_HOME_HERO_ROTATION_SECONDS}
              max={MAX_HOME_HERO_ROTATION_SECONDS}
              value={values.rotationIntervalSeconds}
              disabled={values.activeBannerIds.length < 2}
              onChange={(event) => {
                const nextValue = Number(event.target.value)
                if (Number.isFinite(nextValue)) {
                  setValues((current) => ({
                    ...current,
                    rotationIntervalSeconds: nextValue,
                  }))
                }
              }}
            />
            <p className="text-sm text-muted-foreground">
              Used when two or more banners are active. Choose 3 to 30 seconds.
            </p>
          </div>
        </section>

        {selectedBanner ? (
          <section className="space-y-6 rounded-lg border p-4 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
              <div className="min-w-0 flex-1 space-y-2">
                <Label htmlFor="banner-name">Banner Name</Label>
                <Input
                  id="banner-name"
                  value={selectedBanner.name}
                  onChange={(event) => patchBanner('name', event.target.value)}
                  placeholder="Internal banner name"
                />
                <p className="text-sm text-muted-foreground">
                  This name is only shown to administrators.
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="h-11">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete {selectedBanner.name}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This removes the saved banner and takes it out of the active carousel. The
                      change is permanent after you save.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep Banner</AlertDialogCancel>
                    <AlertDialogAction variant="destructive" onClick={deleteSelectedBanner}>
                      Delete Banner
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            <Separator />

            <div className="space-y-3">
              <Label>Background Image</Label>
              <div
                className="relative aspect-[21/9] w-full overflow-hidden rounded-md border bg-muted"
                style={backgroundStyle}
              >
                {!selectedBanner.backgroundImageUrl && (
                  <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                    <ImageIcon className="h-8 w-8" />
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" className="h-11" onClick={() => setBgPickerOpen(true)}>
                  {selectedBanner.backgroundImageUrl ? 'Replace Background' : 'Choose Background'}
                </Button>
                {selectedBanner.backgroundImageUrl && (
                  <Button
                    variant="ghost"
                    className="h-11"
                    onClick={() => {
                      patchBanner('backgroundImageUrl', '')
                      patchBanner('backgroundMediaId', '')
                    }}
                  >
                    <X className="mr-2 h-4 w-4" />
                    Remove
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <Label>
                Center Logo <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <div className="flex h-32 w-full items-center justify-center overflow-hidden rounded-md border bg-muted/30">
                {selectedBanner.logoImageUrl ? (
                  <img
                    src={selectedBanner.logoImageUrl}
                    alt="Logo preview"
                    className="max-h-full max-w-[60%] object-contain"
                  />
                ) : (
                  <ImageIcon className="h-8 w-8 text-muted-foreground" />
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" className="h-11" onClick={() => setLogoPickerOpen(true)}>
                  {selectedBanner.logoImageUrl ? 'Replace Logo' : 'Choose Logo'}
                </Button>
                {selectedBanner.logoImageUrl && (
                  <Button
                    variant="ghost"
                    className="h-11"
                    onClick={() => {
                      patchBanner('logoImageUrl', '')
                      patchBanner('logoMediaId', '')
                    }}
                  >
                    <X className="mr-2 h-4 w-4" />
                    Remove
                  </Button>
                )}
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Body Text (Markdown)</Label>
              <MarkdownField
                value={selectedBanner.bodyMarkdown}
                onChange={(value) => patchBanner('bodyMarkdown', value)}
                placeholder={'# Applications Open\n\nSubmit by **June 15**. Markdown supported.'}
                height={280}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="cta-label">Button Label</Label>
                <Input
                  id="cta-label"
                  placeholder="e.g. Apply Now"
                  value={selectedBanner.ctaLabel}
                  onChange={(event) => patchBanner('ctaLabel', event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cta-url">Button Link URL</Label>
                <Input
                  id="cta-url"
                  placeholder="/applications/example or https://example.com"
                  value={selectedBanner.ctaUrl}
                  onChange={(event) => patchBanner('ctaUrl', event.target.value)}
                />
              </div>
              <div className="flex min-h-11 items-center gap-2 md:col-span-2">
                <Switch
                  id="cta-new-tab"
                  checked={selectedBanner.ctaOpenInNewTab}
                  onCheckedChange={(checked) => patchBanner('ctaOpenInNewTab', checked)}
                />
                <Label htmlFor="cta-new-tab" className="flex items-center gap-1 font-normal">
                  Open link in a new tab <ExternalLink className="h-3 w-3" />
                </Label>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="overlay">
                  Overlay Darkness ({Math.round(selectedBanner.overlayOpacity * 100)}%)
                </Label>
                <input
                  id="overlay"
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={selectedBanner.overlayOpacity}
                  onChange={(event) => patchBanner('overlayOpacity', Number(event.target.value))}
                  className="min-h-11 w-full"
                />
                <p className="text-sm text-muted-foreground">
                  Darkens the background image so text stays readable.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="text-color">Text Color</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="color"
                    value={validColorInput(selectedBanner.textColor)}
                    onChange={(event) => patchBanner('textColor', event.target.value)}
                    className="h-11 w-14 cursor-pointer rounded border bg-transparent p-1"
                    aria-label="Choose text color"
                  />
                  <Input
                    id="text-color"
                    value={selectedBanner.textColor}
                    onChange={(event) => patchBanner('textColor', event.target.value)}
                    placeholder="#ffffff"
                    className="w-32 font-mono uppercase"
                  />
                  {['#ffffff', '#000000', '#ff9d76'].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => patchBanner('textColor', preset)}
                      className="h-11 w-11 rounded border"
                      style={{ backgroundColor: preset }}
                      aria-label={`Use ${preset}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>
        ) : (
          <section className="flex min-h-80 items-center justify-center rounded-lg border border-dashed p-6 text-center">
            <div>
              <ImageIcon className="mx-auto h-10 w-10 text-muted-foreground" />
              <h2 className="mt-4 text-lg font-semibold">Create a banner to get started</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Saved banners can be edited at any time and activated when needed.
              </p>
              <Button className="mt-4 h-11" onClick={addBanner}>
                <Plus className="mr-2 h-4 w-4" />
                Create Banner
              </Button>
            </div>
          </section>
        )}
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-6xl overflow-hidden p-0">
          <DialogHeader className="px-6 pb-2 pt-6">
            <DialogTitle>Carousel Preview</DialogTitle>
          </DialogHeader>
          <div className="max-h-[80vh] overflow-y-auto border-t">
            <HomeHeroCarouselView config={previewValues} preview />
          </div>
        </DialogContent>
      </Dialog>

      {selectedBanner && (
        <>
          <MediaPickerSheet
            open={bgPickerOpen}
            onClose={() => setBgPickerOpen(false)}
            title="Background Image"
            description="Select a wide image for the banner background. Use the focus point to control how it crops."
            withFocusPoint={true}
            currentMediaId={selectedBanner.backgroundMediaId || undefined}
            currentFocusX={selectedBanner.backgroundFocusX}
            currentFocusY={selectedBanner.backgroundFocusY}
            onAssign={(mediaId, mediaUrl, focusX, focusY) => {
              setValues((current) => ({
                ...current,
                banners: current.banners.map((banner) =>
                  banner.id === selectedBanner.id
                    ? {
                        ...banner,
                        backgroundMediaId: mediaId,
                        backgroundImageUrl: mediaUrl,
                        backgroundFocusX: focusX,
                        backgroundFocusY: focusY,
                      }
                    : banner
                ),
              }))
            }}
          />

          <MediaPickerSheet
            open={logoPickerOpen}
            onClose={() => setLogoPickerOpen(false)}
            title="Center Logo"
            description="Select a logo. PNG or SVG files with transparent backgrounds work best."
            withFocusPoint={false}
            currentMediaId={selectedBanner.logoMediaId || undefined}
            onAssign={(mediaId, mediaUrl) => {
              setValues((current) => ({
                ...current,
                banners: current.banners.map((banner) =>
                  banner.id === selectedBanner.id
                    ? { ...banner, logoMediaId: mediaId, logoImageUrl: mediaUrl }
                    : banner
                ),
              }))
            }}
          />
        </>
      )}
    </div>
  )
}
